import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Plus, Trash2 } from "react-feather";
import { Autocomplete, FormControl, TextField } from "@mui/material";
import tenantStyles from "../../tenantlist.module.css";
import styles from "./addTenant.module.css";
import { PhoneField } from "./fields";

const EMPTY_TENANT = { first_name: "", last_name: "", email: "", phone: "", old_id: "", type: "" };
const TENANT_FIELDS = ["first_name", "last_name", "email", "phone"];

const formatPhone = (phone) => String(phone || "").replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");

const StepTenants = ({ control, errors, fields, append, remove, setValue, clearErrors, tenantOptions, selectedProperty }) => {
  // How each tenant card is filled ("select" a prospect / moved-out tenant, or "manual"), keyed by field id.
  const [modes, setModes] = useState({});
  // The prospect / moved-out tenant picked on a "select" card, keyed by field id.
  const [picked, setPicked] = useState({});

  useEffect(() => {
    setModes((prev) => {
      const next = { ...prev };
      fields.forEach((item, index) => {
        if (!next[item.id]) next[item.id] = index === 0 ? "select" : "manual";
      });
      return next;
    });
  }, [fields]);

  const setTenantValues = (index, values) => {
    Object.keys(values).forEach((key) => {
      setValue(`tenants.${index}.${key}`, values[key], { shouldValidate: TENANT_FIELDS.includes(key) && values[key] !== "" });
    });
  };

  const changeMode = (item, index, mode) => {
    if (modes[item.id] === mode) return;

    setModes((prev) => ({ ...prev, [item.id]: mode }));
    setPicked((prev) => ({ ...prev, [item.id]: null }));

    if (mode === "manual") {
      // Keep what was filled in, but it is now a manual entry, not a linked record.
      setTenantValues(index, { old_id: "", type: "" });
    } else {
      setTenantValues(index, EMPTY_TENANT);
      clearErrors(`tenants.${index}`);
    }
  };

  const pickTenant = (item, index, option) => {
    if (!option) return;

    let firstName = option.first_name;
    if (option.middle_name) {
      firstName += " " + option.middle_name;
    }

    setPicked((prev) => ({ ...prev, [item.id]: option }));
    setTenantValues(index, {
      old_id: option.id,
      type: option.type,
      first_name: firstName || "",
      last_name: option.last_name || "",
      email: option.email || "",
      phone: option.phone || "",
    });
  };

  const clearPick = (item, index) => {
    setPicked((prev) => ({ ...prev, [item.id]: null }));
    setTenantValues(index, EMPTY_TENANT);
  };

  return (
    <div className={styles.StepBody}>
      {fields.map((item, index) => {
        const mode = modes[item.id] || (index === 0 ? "select" : "manual");
        const pickedTenant = picked[item.id];
        const tenantErrors = errors?.tenants?.[index];

        return (
          <div className={styles.TenantCard} key={item.id}>
            <div className={styles.TenantCardHead}>
              <span>{index === 0 ? "Tenant # 1 (Primary)" : `Tenant # ${index + 1}`}</span>
              {fields.length > 1 && (<button type="button" className={styles.DeleteTenantBU} onClick={() => remove(index)}><Trash2 /> Delete Tenant</button>)}
            </div>

            <div role="tablist" aria-label="How to add this tenant" className={styles.Tabs}>
              <button type="button" role="tab" aria-selected={mode === "select"} className={mode === "select" ? styles.TabActive : ""} onClick={() => changeMode(item, index, "select")}>Select Prospect / Moved Out</button>
              <button type="button" role="tab" aria-selected={mode === "manual"} className={mode === "manual" ? styles.TabActive : ""} onClick={() => changeMode(item, index, "manual")}>Enter Manually</button>
            </div>

            <div className={styles.TenantCardBody}>
              <Controller name={`tenants.${index}.old_id`} control={control} defaultValue={item.old_id || ""} render={(field) => (<input type="hidden" value={field.value || ""} readOnly />)} />
              <Controller name={`tenants.${index}.type`} control={control} defaultValue={item.type || ""} render={(field) => (<input type="hidden" value={field.value || ""} readOnly />)} />

              {mode === "select" && !pickedTenant && (<div className="FormGroup">
                <FormControl fullWidth>
                  <Autocomplete
                    options={tenantOptions}
                    value={null}
                    noOptionsText={selectedProperty ? "No prospects or moved-out tenants found" : "Please select a property first"}
                    renderOption={(props, option) => (<li {...props} key={`${option.type}-${option.id}`}>
                      <div className={tenantStyles.AutoOption}>
                        <div>
                          <div className={tenantStyles.AutoName}>{option.label}</div>
                          <div className={tenantStyles.AutoPhone}>{formatPhone(option.phone)}</div>
                        </div>
                        <span className={option.typeL === "Prospect" ? tenantStyles.Prospect : tenantStyles.MoveOut}>{option.typeL}</span>
                      </div>
                    </li>)}
                    renderInput={(params) => (<TextField {...params} label="Search prospect or moved-out tenant*" />)}
                    onChange={(event, newValue) => pickTenant(item, index, newValue)}
                  />
                </FormControl>
                {tenantErrors && (<p className={tenantStyles.ErrorM}>Please select a prospect or moved-out tenant.</p>)}
              </div>)}

              {mode === "select" && pickedTenant && (<div className={styles.PickedTenant}>
                <div>
                  <p className={styles.PickedName}>
                    {pickedTenant.label}
                    <span className={styles.PickedChip}>{pickedTenant.typeL}</span>
                  </p>
                  <p>{pickedTenant.email}</p>
                  <p>{formatPhone(pickedTenant.phone)}</p>
                </div>
                <button type="button" className={styles.OutlineBU} onClick={() => clearPick(item, index)}>Change</button>
              </div>)}

              {/* Always mounted so the values survive tab switches; only shown when entering manually. */}
              <div className={mode === "manual" ? styles.Grid2 : styles.Hidden}>
                <div className="FormGroup">
                  <Controller
                    name={`tenants.${index}.first_name`}
                    control={control}
                    defaultValue={item.first_name || ""}
                    render={(field) => (<TextField onChange={(e) => field.onChange(e)} label="First name*" value={field.value} InputLabelProps={{ shrink: !!field.value }} fullWidth />)}
                  />
                  {tenantErrors?.first_name && (<p className={tenantStyles.ErrorM}>{tenantErrors.first_name.message}</p>)}
                </div>
                <div className="FormGroup">
                  <Controller
                    name={`tenants.${index}.last_name`}
                    control={control}
                    defaultValue={item.last_name || ""}
                    render={(field) => (<TextField onChange={(e) => field.onChange(e)} label="Last name*" value={field.value} InputLabelProps={{ shrink: !!field.value }} fullWidth />)}
                  />
                  {tenantErrors?.last_name && (<p className={tenantStyles.ErrorM}>{tenantErrors.last_name.message}</p>)}
                </div>
                <div className="FormGroup">
                  <Controller
                    name={`tenants.${index}.email`}
                    control={control}
                    defaultValue={item.email || ""}
                    render={(field) => (<TextField onChange={(e) => field.onChange(e)} label="Email*" value={field.value} InputLabelProps={{ shrink: !!field.value }} fullWidth />)}
                  />
                  {tenantErrors?.email && (<p className={tenantStyles.ErrorM}>{tenantErrors.email.message}</p>)}
                </div>
                <div className="FormGroup">
                  <Controller
                    name={`tenants.${index}.phone`}
                    control={control}
                    defaultValue={item.phone || ""}
                    render={(field) => (<TextField onChange={(e) => field.onChange(e)} label="Phone No*" value={field.value} InputLabelProps={{ shrink: !!field.value }} InputProps={{ inputComponent: PhoneField }} fullWidth />)}
                  />
                  {tenantErrors?.phone && (<p className={tenantStyles.ErrorM}>{tenantErrors.phone.message}</p>)}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {fields.length < 3 && (<button type="button" className={styles.OutlineBU} onClick={() => append({ ...EMPTY_TENANT }, false)}><Plus /> Add Another Tenant</button>)}
    </div>
  );
};

export default StepTenants;
